import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Mail, MapPin, Clock, Send, UserCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const formRef = useRef(null);
  const [activeTab, setActiveTab] = useState('contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [contactPersons, setContactPersons] = useState([]);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', company: '', service_type: '', origin: '', destination: '', cargo_description: '', estimated_weight: '', estimated_volume: '', preferred_date: '', additional_info: '' });
  const [pageContent, setPageContent] = useState(null);
  const { language, t } = useSiteContent();
  
  // Set page title
  usePageTitle('contact');
  const [siteSettings, setSiteSettings] = useState({
    company_email: 'info@tetralog.bg',
    company_phone: '+359 887 198 834',
    address_1_name: 'Tetralog Ltd.',
    address_1_street: 'Tsar Osvoboditel 88V',
    address_1_city: '4400 Pazardzhik, Bulgaria',
    address_1_maps_url: '',
    address_2_name: '',
    address_2_street: '',
    address_2_city: '',
    address_2_maps_url: ''
  });

  const serviceTypes = language === 'bg' 
    ? ['Международно преместване', 'Транспорт на лични вещи', 'Експресен транспорт', 'Групажна линия', 'Специализиран товар', 'Друго']
    : ['International Moving', 'Personal Belongings Transport', 'Express Transportation', 'Groupage Line', 'Specialized Cargo', 'Other'];

  // Default labels
  const defaultLabels = language === 'bg' ? {
    subtitle: 'КОНТАКТ',
    title: 'Свържете се',
    titleHighlight: 'с нас',
    description: 'Имате въпрос или се нуждаете от оферта? Нашият екип е готов да ви помогне.',
    callUs: 'Обадете се',
    emailUs: 'Пишете ни',
    workingHours: 'Работно време',
    monFri: 'Пон-Пет: 08:00-17:30',
    contactTab: 'Контакт',
    quoteTab: 'Оферта',
    sendMessage: 'Изпратете съобщение',
    fullName: 'Име и фамилия',
    email: 'Имейл',
    phone: 'Телефон',
    subject: 'Тема',
    message: 'Съобщение',
    send: 'Изпрати съобщение',
    sending: 'Изпращане...',
    requestQuote: 'Заявете оферта',
    company: 'Фирма',
    serviceType: 'Тип услуга',
    selectService: 'Изберете услуга',
    origin: 'Място на изпращане',
    destination: 'Дестинация',
    cargoDescription: 'Описание на товара',
    estimatedWeight: 'Приблизително тегло',
    estimatedVolume: 'Приблизителен обем',
    preferredDate: 'Предпочитана дата',
    submitQuote: 'Изпрати заявка',
    ourLocation: 'Нашето местоположение',
    ourTeam: 'Нашият екип'
  } : {
    subtitle: 'CONTACT US',
    title: 'Get in',
    titleHighlight: 'Touch',
    description: 'Have a question or need a quote? Our team is ready to help you with all your transport and logistics needs.',
    callUs: 'Call Us',
    emailUs: 'Email Us',
    workingHours: 'Working Hours',
    monFri: 'Mon-Fri: 08:00-17:30',
    contactTab: 'Contact',
    quoteTab: 'Get Quote',
    sendMessage: 'Send Us a Message',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    subject: 'Subject',
    message: 'Message',
    send: 'Send Message',
    sending: 'Sending...',
    requestQuote: 'Request a Quote',
    company: 'Company',
    serviceType: 'Service Type',
    selectService: 'Select service type',
    origin: 'Origin',
    destination: 'Destination',
    cargoDescription: 'Cargo Description',
    estimatedWeight: 'Estimated Weight',
    estimatedVolume: 'Estimated Volume',
    preferredDate: 'Preferred Date',
    submitQuote: 'Submit Quote Request',
    ourLocation: 'Our Location',
    ourTeam: 'Our Team'
  };

  // Build labels from pageContent or defaults
  const labels = pageContent ? {
    ...defaultLabels,
    subtitle: language === 'bg' ? (pageContent.hero_subtitle_bg || defaultLabels.subtitle) : (pageContent.hero_subtitle_en || defaultLabels.subtitle),
    title: language === 'bg' ? (pageContent.hero_title_bg || defaultLabels.title) : (pageContent.hero_title_en || defaultLabels.title),
    titleHighlight: language === 'bg' ? (pageContent.hero_title_highlight_bg || defaultLabels.titleHighlight) : (pageContent.hero_title_highlight_en || defaultLabels.titleHighlight),
    description: language === 'bg' ? (pageContent.hero_description_bg || defaultLabels.description) : (pageContent.hero_description_en || defaultLabels.description),
    callUs: language === 'bg' ? (pageContent.call_us_bg || defaultLabels.callUs) : (pageContent.call_us_en || defaultLabels.callUs),
    emailUs: language === 'bg' ? (pageContent.email_us_bg || defaultLabels.emailUs) : (pageContent.email_us_en || defaultLabels.emailUs),
    workingHours: language === 'bg' ? (pageContent.working_hours_bg || defaultLabels.workingHours) : (pageContent.working_hours_en || defaultLabels.workingHours),
    monFri: language === 'bg' ? (pageContent.mon_fri_bg || defaultLabels.monFri) : (pageContent.mon_fri_en || defaultLabels.monFri),
    send: language === 'bg' ? (pageContent.send_bg || defaultLabels.send) : (pageContent.send_en || defaultLabels.send),
    ourLocation: language === 'bg' ? (pageContent.our_location_bg || defaultLabels.ourLocation) : (pageContent.our_location_en || defaultLabels.ourLocation)
  } : defaultLabels;

  // Fetch contact persons, site settings, and page content
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [personsRes, settingsRes, contentRes] = await Promise.all([
          axios.get(`${API}/contact-persons`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/contact-page-content`)
        ]);
        setContactPersons(personsRes.data.persons);
        setSiteSettings(settingsRes.data);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle quote tab and scroll to form
  useEffect(() => {
    const isQuoteRequest = searchParams.get('quote') === 'true';
    
    if (isQuoteRequest) {
      setActiveTab('quote');
      // Scroll to form after page renders and transition completes
      const scrollToForm = () => {
        const element = document.getElementById('quote-form');
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      };
      // Wait for page transition animation to complete
      setTimeout(scrollToForm, 700);
      
      // Clean URL - remove query params and hash without triggering navigation
      const cleanPath = location.pathname;
      window.history.replaceState(null, '', cleanPath);
    }
  }, [searchParams, location.pathname]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, contactForm);
      toast.success(response.data.message);
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) { toast.error('Failed to send message. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/quote`, quoteForm);
      toast.success(`${response.data.message} Reference: ${response.data.reference_id}`);
      setQuoteForm({ name: '', email: '', phone: '', company: '', service_type: '', origin: '', destination: '', cargo_description: '', estimated_weight: '', estimated_volume: '', preferred_date: '', additional_info: '' });
    } catch (error) { toast.error('Failed to submit quote request. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  // Helper to get localized content
  const getLocalized = (person, field) => {
    const localizedField = `${field}_${language}`;
    return person[localizedField] || person[`${field}_en`] || '';
  };

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-20 bg-slate-50">
      <SEO page="contact" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{labels.subtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">{labels.title} <span className="text-[#C41E3A]">{labels.titleHighlight}</span></h1>
          <p className="text-lg text-slate-600 max-w-2xl">{labels.description}</p>
        </div>
      </section>

      {/* Contact Persons Section */}
      {contactPersons.length > 0 && (
        <section className="py-12 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">{labels.ourTeam}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactPersons.map((person) => (
                <Card key={person.id} className="bg-slate-50 border-slate-100 rounded-2xl hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-20 h-20 rounded-full bg-white mx-auto mb-4 flex items-center justify-center overflow-hidden shadow-sm">
                      {person.image_url ? (
                        <img src={person.image_url} alt={getLocalized(person, 'name')} className="w-full h-full object-cover" />
                      ) : (
                        <UserCircle className="w-12 h-12 text-slate-300" />
                      )}
                    </div>
                    <h4 className="font-bold text-slate-900">{getLocalized(person, 'name')}</h4>
                    <p className="text-sm text-[#C41E3A] mb-3">{getLocalized(person, 'position')}</p>
                    <div className="space-y-1 text-sm">
                      <a href={`mailto:${person.email}`} className="flex items-center justify-center gap-2 text-slate-500 hover:text-[#C41E3A]">
                        <Mail className="w-4 h-4" />{person.email}
                      </a>
                      <a href={`tel:${person.phone}`} className="flex items-center justify-center gap-2 text-slate-500 hover:text-[#C41E3A]">
                        <Phone className="w-4 h-4" />{person.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Info Bar */}
      <section className="py-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <a href={`mailto:${siteSettings.company_email || 'info@tetralog.bg'}`} className="flex items-center gap-4 p-4 rounded-2xl hover:bg-white transition-colors" data-testid="contact-email-link">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center"><Mail className="w-5 h-5 text-[#C41E3A]" /></div>
              <div><p className="text-sm text-slate-500">{labels.emailUs}</p><p className="text-slate-900 font-bold">{siteSettings.company_email || 'info@tetralog.bg'}</p></div>
            </a>
            <div className="flex items-center gap-4 p-4">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center"><Clock className="w-5 h-5 text-[#C41E3A]" /></div>
              <div><p className="text-sm text-slate-500">{labels.workingHours}</p><p className="text-slate-900 font-bold">{labels.monFri}</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section id="quote-form" ref={formRef} data-testid="contact-forms-section" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-2 mb-8 bg-white rounded-full p-1">
                  <TabsTrigger value="contact" data-testid="contact-tab" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white">{labels.contactTab}</TabsTrigger>
                  <TabsTrigger value="quote" data-testid="quote-tab" className="rounded-full data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white">{labels.quoteTab}</TabsTrigger>
                </TabsList>

                <TabsContent value="contact">
                  <Card className="bg-white border-slate-100 rounded-3xl"><CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-6">{labels.sendMessage}</h3>
                    <form onSubmit={handleContactSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-slate-600">{labels.fullName} *</Label><Input data-testid="contact-name-input" required value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                        <div><Label className="text-slate-600">{labels.email} *</Label><Input data-testid="contact-email-input" type="email" required value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-slate-600">{labels.phone}</Label><Input data-testid="contact-phone-input" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" placeholder="+359 XXX XXX XXX" /></div>
                        <div><Label className="text-slate-600">{labels.subject} *</Label><Input data-testid="contact-subject-input" required value={contactForm.subject} onChange={(e) => setContactForm({...contactForm, subject: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <div><Label className="text-slate-600">{labels.message} *</Label><Textarea data-testid="contact-message-input" required value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl min-h-[120px]" /></div>
                      <button type="submit" data-testid="contact-submit-btn" disabled={isSubmitting} className="btn-modern btn-primary btn-shimmer">
                        {isSubmitting ? labels.sending : <><Send className="w-4 h-4" />{labels.send}</>}
                      </button>
                    </form>
                  </CardContent></Card>
                </TabsContent>

                <TabsContent value="quote">
                  <Card className="bg-white border-slate-100 rounded-3xl"><CardContent className="p-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-6">{labels.requestQuote}</h3>
                    <form onSubmit={handleQuoteSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-slate-600">{labels.fullName} *</Label><Input data-testid="quote-name-input" required value={quoteForm.name} onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                        <div><Label className="text-slate-600">{labels.email} *</Label><Input data-testid="quote-email-input" type="email" required value={quoteForm.email} onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-slate-600">{labels.phone} *</Label><Input data-testid="quote-phone-input" required value={quoteForm.phone} onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                        <div><Label className="text-slate-600">{labels.company}</Label><Input data-testid="quote-company-input" value={quoteForm.company} onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <div><Label className="text-slate-600">{labels.serviceType} *</Label>
                        <Select value={quoteForm.service_type} onValueChange={(value) => setQuoteForm({...quoteForm, service_type: value})}>
                          <SelectTrigger data-testid="quote-service-select" className="mt-1 bg-slate-50 border-slate-200 rounded-xl"><SelectValue placeholder={labels.selectService} /></SelectTrigger>
                          <SelectContent className="bg-white border-slate-200 rounded-xl">{serviceTypes.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label className="text-slate-600">{labels.origin} *</Label><Input data-testid="quote-origin-input" required value={quoteForm.origin} onChange={(e) => setQuoteForm({...quoteForm, origin: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                        <div><Label className="text-slate-600">{labels.destination} *</Label><Input data-testid="quote-destination-input" required value={quoteForm.destination} onChange={(e) => setQuoteForm({...quoteForm, destination: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <div><Label className="text-slate-600">{labels.cargoDescription} *</Label><Textarea data-testid="quote-cargo-input" required value={quoteForm.cargo_description} onChange={(e) => setQuoteForm({...quoteForm, cargo_description: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><Label className="text-slate-600">{labels.estimatedWeight}</Label><Input data-testid="quote-weight-input" value={quoteForm.estimated_weight} onChange={(e) => setQuoteForm({...quoteForm, estimated_weight: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" placeholder="e.g., 500 kg" /></div>
                        <div><Label className="text-slate-600">{labels.estimatedVolume}</Label><Input data-testid="quote-volume-input" value={quoteForm.estimated_volume} onChange={(e) => setQuoteForm({...quoteForm, estimated_volume: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" placeholder="e.g., 10 m³" /></div>
                        <div><Label className="text-slate-600">{labels.preferredDate}</Label><Input data-testid="quote-date-input" type="date" value={quoteForm.preferred_date} onChange={(e) => setQuoteForm({...quoteForm, preferred_date: e.target.value})} className="mt-1 bg-slate-50 border-slate-200 rounded-xl" /></div>
                      </div>
                      <button type="submit" data-testid="quote-submit-btn" disabled={isSubmitting} className="btn-modern btn-primary btn-shimmer">{isSubmitting ? labels.sending : labels.submitQuote}</button>
                    </form>
                  </CardContent></Card>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="bg-white border-slate-100 rounded-3xl"><CardContent className="p-6">
                <h4 className="text-xl font-bold text-slate-900 mb-4">{labels.ourLocation}</h4>
                <div className="space-y-4">
                  {/* Address 1 */}
                  {(siteSettings.address_1_name || siteSettings.address_1_street || siteSettings.address_1_city) && (
                    siteSettings.address_1_maps_url ? (
                      <a href={siteSettings.address_1_maps_url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-[#C41E3A] transition-colors group">
                        <MapPin className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" />
                        <div>
                          {siteSettings.address_1_name && <p className="text-slate-900 group-hover:text-[#C41E3A]">{siteSettings.address_1_name}</p>}
                          <p className="text-slate-500 text-sm group-hover:text-[#C41E3A]/70">
                            {siteSettings.address_1_street && <>{siteSettings.address_1_street}<br /></>}
                            {siteSettings.address_1_city}
                          </p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" />
                        <div>
                          {siteSettings.address_1_name && <p className="text-slate-900">{siteSettings.address_1_name}</p>}
                          <p className="text-slate-500 text-sm">
                            {siteSettings.address_1_street && <>{siteSettings.address_1_street}<br /></>}
                            {siteSettings.address_1_city}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                  {/* Address 2 */}
                  {(siteSettings.address_2_name || siteSettings.address_2_street || siteSettings.address_2_city) && (
                    siteSettings.address_2_maps_url ? (
                      <a href={siteSettings.address_2_maps_url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-[#C41E3A] transition-colors group">
                        <MapPin className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" />
                        <div>
                          {siteSettings.address_2_name && <p className="text-slate-900 group-hover:text-[#C41E3A]">{siteSettings.address_2_name}</p>}
                          <p className="text-slate-500 text-sm group-hover:text-[#C41E3A]/70">
                            {siteSettings.address_2_street && <>{siteSettings.address_2_street}<br /></>}
                            {siteSettings.address_2_city}
                          </p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" />
                        <div>
                          {siteSettings.address_2_name && <p className="text-slate-900">{siteSettings.address_2_name}</p>}
                          <p className="text-slate-500 text-sm">
                            {siteSettings.address_2_street && <>{siteSettings.address_2_street}<br /></>}
                            {siteSettings.address_2_city}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                  <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" /><div><p className="text-slate-900">{siteSettings.company_email || 'info@tetralog.bg'}</p><p className="text-slate-500 text-sm">24h {language === 'bg' ? 'време за отговор' : 'response time'}</p></div></div>
                  <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-[#C41E3A] flex-shrink-0 mt-0.5" /><div><p className="text-slate-900">{labels.workingHours}</p><p className="text-slate-500 text-sm">{labels.monFri}</p></div></div>
                </div>
              </CardContent></Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
